import React, { useEffect, useState, useRef } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  doc, getDoc, collection, getDocs, query, where, updateDoc
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import imageCompression from "browser-image-compression";
import { db } from "../firebase/firebase";
import { useNavigate } from "react-router-dom";

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmtKey = k => k.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase());

const SECTIONS = [
  { id: "overview",  label: "Overview",          icon: "👤", fields: ["name","age","location","occupation","gender","educationLevel"] },
  { id: "career",    label: "Education & Career", icon: "🎓", fields: ["educationBackground","careerGoals","importanceOfPartnerEducation"] },
  { id: "values",    label: "Values",             icon: "💡", fields: ["personalityTraits","nonNegotiableQualities","longTermGoals"] },
  { id: "lifestyle", label: "Lifestyle",          icon: "✨", fields: ["hobbies","lifestyleChoices","healthConsiderations"] },
  { id: "family",    label: "Family & Culture",   icon: "🌍", fields: ["culturalBackground","familyExpectations","familyTraditions","importanceOfCulturalBackground","roleOfFamily"] },
  { id: "marriage",  label: "Marriage",           icon: "💍", fields: ["marriageRoles","children","finances","householdDuties","futureVision","conflictHandling"] },
];

const KEY_FIELDS = ["name","age","gender","location","occupation","educationLevel","careerGoals",
  "personalityTraits","hobbies","culturalBackground","children","longTermGoals"];

const calcCompleteness = (d) => {
  if (!d) return 0;
  const filled = KEY_FIELDS.filter(f => {
    const v = d[f];
    return v && (Array.isArray(v) ? v.length > 0 : String(v).trim() !== "");
  });
  return Math.round((filled.length / KEY_FIELDS.length) * 100);
};

// ─── sub-components ───────────────────────────────────────────────────────────
function Toast({ message, visible, type = "success" }) {
  return (
    <div className={`ma-toast ${visible ? "ma-toast--show" : ""} ma-toast--${type}`} role="status" aria-live="polite">
      <span className="ma-toast__icon">{type === "success" ? "✓" : "!"}</span>
      {message}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="ma-root">
      <div className="ma-hero ma-hero--skeleton">
        <div className="ma-hero__inner">
          <div className="ma-skeleton-avatar" />
          <div className="ma-skeleton-lines">
            <div className="ma-skeleton-line ma-skeleton-line--wide" />
            <div className="ma-skeleton-line ma-skeleton-line--narrow" />
          </div>
        </div>
      </div>
      <div className="ma-container">
        {[1,2,3].map(i => (
          <div key={i} className="ma-section ma-section--skeleton">
            <div className="ma-skeleton-line ma-skeleton-line--section-title" />
            <div className="ma-skeleton-grid">
              {[1,2,3,4].map(j => <div key={j} className="ma-skeleton-field" />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ icon, count, label, onClick }) {
  return (
    <button className="ma-stat" onClick={onClick} aria-label={`${count} ${label}`}>
      <span className="ma-stat__icon">{icon}</span>
      <span className="ma-stat__count">{count}</span>
      <span className="ma-stat__label">{label}</span>
    </button>
  );
}

function CompletenessBar({ pct }) {
  const color = pct >= 80 ? "var(--color-success)" : pct >= 50 ? "var(--gold)" : "var(--terracotta)";
  const label = pct >= 80 ? "Great profile!" : pct >= 50 ? "Looking good — add more details" : "Complete your profile to attract matches";
  return (
    <div className="ma-completeness">
      <div className="ma-completeness__header">
        <span className="ma-completeness__label">{label}</span>
        <span className="ma-completeness__pct" style={{ color }}>{pct}% complete</span>
      </div>
      <div className="ma-completeness__track">
        <div className="ma-completeness__fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function PhotoGrid({ photos, onUpload, uploading }) {
  const inputRef = useRef();
  const MAX = 5;
  return (
    <div className="ma-photos">
      <div className="ma-photos__grid">
        {photos.map((url, i) => (
          <div key={i} className={`ma-photo ${i === 0 ? "ma-photo--primary" : ""}`}>
            <img src={url} alt={`Profile photo ${i + 1}`} className="ma-photo__img" loading="lazy" />
            {i === 0 && <span className="ma-photo__badge">Main</span>}
          </div>
        ))}
        {photos.length < MAX && (
          <button
            className={`ma-photo ma-photo--upload ${uploading ? "ma-photo--uploading" : ""}`}
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            aria-label="Upload photo"
          >
            {uploading ? (
              <span className="ma-upload-spinner" />
            ) : (
              <>
                <span className="ma-upload-icon">+</span>
                <span className="ma-upload-label">Add photo</span>
                <span className="ma-upload-count">{photos.length}/{MAX}</span>
              </>
            )}
            <input ref={inputRef} hidden type="file" accept="image/*" onChange={e => onUpload(e.target.files[0])} />
          </button>
        )}
      </div>
      {photos.length === 0 && (
        <p className="ma-photos__hint">Add photos to show up in more matches — profiles with photos get 8× more views.</p>
      )}
    </div>
  );
}

function SectionBlock({ section, userData }) {
  const [open, setOpen] = useState(true);
  const hasData = section.fields.some(f => {
    const v = userData[f];
    return v && (Array.isArray(v) ? v.length > 0 : String(v).trim() !== "");
  });

  return (
    <div className={`ma-section ${!hasData ? "ma-section--empty" : ""}`}>
      <button className="ma-section__header" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <div className="ma-section__title-wrap">
          <span className="ma-section__icon">{section.icon}</span>
          <span className="ma-section__title">{section.label}</span>
          {!hasData && <span className="ma-section__empty-badge">Incomplete</span>}
        </div>
        <span className={`ma-section__chevron ${open ? "ma-section__chevron--open" : ""}`}>›</span>
      </button>

      {open && (
        <div className="ma-section__body">
          {!hasData ? (
            <p className="ma-section__empty-msg">No information added yet. <a href="/signup" className="ma-link">Edit profile</a></p>
          ) : (
            <div className="ma-fields">
              {section.fields.map(f => {
                const v = userData[f];
                if (!v && v !== 0) return null;
                const display = Array.isArray(v) ? v : String(v);
                return (
                  <div key={f} className="ma-field">
                    <span className="ma-field__key">{fmtKey(f)}</span>
                    {Array.isArray(display) ? (
                      <div className="ma-field__chips">
                        {display.map((item, i) => <span key={i} className="ma-chip">{item}</span>)}
                      </div>
                    ) : (
                      <span className="ma-field__val">{display}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── main ──────────────────────────────────────────────────────────────────────
export default function MyAccount() {
  const [userData,     setUserData]     = useState(null);
  const [photos,       setPhotos]       = useState([]);
  const [likesCount,   setLikesCount]   = useState(0);
  const [matchesCount, setMatchesCount] = useState(0);
  const [uploading,    setUploading]    = useState(false);
  const [toast,        setToast]        = useState({ visible: false, message: "", type: "success" });
  const [userId,       setUserId]       = useState(null);

  const navigate = useNavigate();
  const storage  = getStorage();

  const showToast = (message, type = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 2800);
  };

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { navigate("/signin"); return; }
      setUserId(user.uid);
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) {
          const d = snap.data();
          setUserData(d);
          setPhotos(d.photos || []);
        }
        const [likesSnap, matchesSnap] = await Promise.all([
          getDocs(query(collection(db, "likes"), where("toUserId",   "==", user.uid))),
          getDocs(query(collection(db, "likes"), where("fromUserId", "==", user.uid))),
        ]);
        setLikesCount(likesSnap.size);
        setMatchesCount(matchesSnap.size);
      } catch (err) {
        console.error(err);
      }
    });
    return () => unsub();
  }, [navigate]);

  const uploadPhoto = async (file) => {
    if (!file || !userId) return;
    if (photos.length >= 5) { showToast("Maximum 5 photos allowed", "error"); return; }
    setUploading(true);
    try {
      const compressed = await imageCompression(file, { maxSizeMB: 0.4, maxWidthOrHeight: 1200, useWebWorker: true });
      const storageRef  = ref(storage, `profilePhotos/${userId}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, compressed);
      const url = await getDownloadURL(storageRef);
      const updated = [...photos, url];
      setPhotos(updated);
      await updateDoc(doc(db, "users", userId), { photos: updated });
      showToast("Photo uploaded successfully");
    } catch (err) {
      console.error(err);
      showToast("Upload failed. Please try again.", "error");
    } finally {
      setUploading(false);
    }
  };

  if (!userData) return <Skeleton />;

  const completeness = calcCompleteness(userData);
  const primaryPhoto = photos[0] || null;

  return (
    <div className="ma-root">
      <Toast message={toast.message} visible={toast.visible} type={toast.type} />

      {/* hero */}
      <div className="ma-hero">
        <div className="ma-hero__bg" />
        <div className="ma-hero__inner">
          <button className="ma-back" onClick={() => navigate(-1)} aria-label="Go back">← Back</button>

          <div className="ma-hero__profile">
            <div className="ma-avatar-wrap">
              {primaryPhoto
                ? <img src={primaryPhoto} alt={userData.name} className="ma-avatar" />
                : <div className="ma-avatar ma-avatar--empty">
                    <span>{userData.name?.[0]?.toUpperCase() || "?"}</span>
                  </div>
              }
            </div>
            <div className="ma-hero__info">
              <h1 className="ma-hero__name">{userData.name}{userData.age ? `, ${userData.age}` : ""}</h1>
              <div className="ma-hero__meta">
                {userData.location   && <span className="ma-hero__meta-item">📍 {userData.location}</span>}
                {userData.occupation && <span className="ma-hero__meta-item">💼 {userData.occupation}</span>}
                {userData.educationLevel && <span className="ma-hero__meta-item">🎓 {userData.educationLevel}</span>}
              </div>
              <CompletenessBar pct={completeness} />
            </div>
          </div>

          <div className="ma-stats">
            <StatCard icon="♥"  count={likesCount}    label="Likes received"  onClick={() => navigate("/likes")} />
            <StatCard icon="💍" count={matchesCount}   label="Profiles liked"  onClick={() => navigate("/matches")} />
            <StatCard icon="📷" count={photos.length}  label={`Photo${photos.length !== 1 ? "s" : ""}`} onClick={() => {}} />
          </div>
        </div>
      </div>

      {/* content */}
      <div className="ma-container">
        <div className="ma-section">
          <div className="ma-section__header ma-section__header--static">
            <div className="ma-section__title-wrap">
              <span className="ma-section__icon">📷</span>
              <span className="ma-section__title">Profile Photos</span>
            </div>
          </div>
          <div className="ma-section__body">
            <PhotoGrid photos={photos} onUpload={uploadPhoto} uploading={uploading} />
          </div>
        </div>

        {SECTIONS.map(s => (
          <SectionBlock key={s.id} section={s} userData={userData} />
        ))}

        <div className="ma-edit-cta">
          <p className="ma-edit-cta__text">Want to update your profile?</p>
          <a href="/signup" className="ma-btn-primary">Edit profile</a>
        </div>
      </div>
    </div>
  );
}